import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { Address, Hex } from 'viem';
import { SchemaOrderDto } from '../generated/apiTypes';
import { encodeSubaccountName } from '../lib/subaccountHelpers';

export const DEFAULT_SUBACCOUNT = encodeSubaccountName('primary');
// NOTE: SignatureType enums need to match names in signatures defined at `ExchangeGateway.sol`.
//
// @see: src/ExchangeGateway.sol (https://github.com/meridianxyz/contracts/blob/master/src/ExchangeGateway.sol)
export enum SignatureType {
  // --- Onchain --- //
  LINK_SIGNER = 'LinkSigner',
  REVOKE_LINKED_SIGNER = 'RevokeLinkedSigner',
  TRADE_ORDER = 'TradeOrder',
  INITIATE_WITHDRAW = 'InitiateWithdraw',

  // --- Offchain --- //
  CANCEL_ORDER = 'CancelOrder',
}

export interface CancelOrderSignatureMessage {
  sender: Address;
  subaccount: Hex;
  nonce: bigint;
  orderIds: Hex[];
}

export const signingMethods = ['one-click', 'manual'] as const;
export type SigningMethod = (typeof signingMethods)[number];

export const OPEN_ORDER_STATUSES = [
  'NEW',
  'FILLED_PARTIAL',
  'SUBMITTED',
  'PENDING',
] satisfies SchemaOrderDto['status'][];

export const ORDER_DEFAULT_EXPIRATION = 6652800; /* 77 days */

export const CLOSED_ORDER_STATUSES = [
  'FILLED',
  'CANCELED',
  'EXPIRED',
] satisfies SchemaOrderDto['status'][];

export const ORDER_STOP_TYPES = {
  GAIN: 0,
  LOSS: 1,
} as const;

export const TRADE_PAGE_TABLE_ROWS = 12;
export const TRADE_PAGE_SCROLLABLE_TABLE_ROWS = 100;
export const PORTFOLIO_PAGE_TABLE_ROWS = 20;
export const PORTFOLIO_PAGE_TABLE_ROW_OPTIONS = [20, 50, 100];

// Estimate for deposits and withdrawals
export const ESTIMATED_SEQUENCER_PROCESSING_TIME = 7 * 1000;

export const PRICE_UPDATE_FREQUENCY = 2000;

// The start date for the portfolio date range pickers.
export const PORTFOLIO_DATE_FILTER_START = new Date(2024, 0, 1);

// See `MAX_ORDER_EXPIRY` in sequencer.
export const MAX_ORDER_EXPIRY_DAYS = 77;
// Max expiry buffer, to avoid `expiresAt exceeds maximum allowed value error`.
export const MAX_ORDER_EXPIRY_BUFFER_MS = secondsToMilliseconds(60);

export const FOOTER_HEIGHT = 37;

export const CANCEL_ALL_ORDERS_LIMIT = 500;

export const DEFAULT_FAVORITE_PRODUCTS = ['ETHUSD', 'BTCUSD', 'ENAUSD'] as const;

// Stale time for queries that are invalidated by WebSocket updates
export const WS_INVALIDATED_QUERY_STALE_TIME = minutesToMilliseconds(3);

export const SIGNER_EXPIRY_BUFFER_MS = minutesToMilliseconds(5);

export const TESTNET_FORM_URL = 'https://register.ethereal.trade/';

export const USD_DECIMALS = 2; // Number of decimal places for USD values
